import React, { useEffect, useState } from "react";
import { useObserver } from "mobx-react";
import { useStores } from "../../store";
import { PrimaryButton } from "../reusble_components/buttons/primaryButton";
import { toJS } from "mobx";
import Popup from "./Components/skipPopup";

const QUESTION_TYPES = [
  { label: "Yes/No", value: "yes/No" },
  { label: "Multiple Choice", value: "multipleChoice" },
  { label: "Short Answer", value: "shortAnswer" },
  { label: "Long Answer", value: "longAnswer" },
];

const defaultOptionKeys = ["a", "b", "c", "d"];

function defaultQuestions() {
  return [
    {
      question:
        "Are you legally authorized to work in this country without sponsorship?",
      type: "yes/No",
      options: { a: "Yes", b: "No" },
      disqualifyIfIncorrect: false,
      expectedAnswer: "a",
    },
    {
      question:
        "Briefly describe your most relevant experience for this role (1–2 sentences).",
      type: "shortAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
    {
      question: "Why are you interested in this role or internship?",
      type: "longAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
    {
      question:
        "Are you currently working or studying, and what is your availability?",
      type: "longAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
    {
      question: "Can you relocate or work from office if required?",
      type: "yes/No",
      options: { a: "Yes", b: "No" },
      disqualifyIfIncorrect: false,
      expectedAnswer: "a",
    },
    {
      question: "Do you have any relevant experience or projects?",
      type: "longAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
    {
      question: "What are your long term goals?",
      type: "longAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
    {
      question: "What are your expectations from this opportunity?",
      type: "longAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
    {
      question: "How much salary/stipend are you expecting?",
      type: "shortAnswer",
      options: null,
      disqualifyIfIncorrect: false,
      expectedAnswer: "",
    },
  ];
}

function emptyQuestion() {
  return {
    question: "",
    type: "yes/No",
    options: { a: "Yes", b: "No" },
    disqualifyIfIncorrect: false,
    expectedAnswer: "a",
  };
}

const TrashIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default function Prescreening({ isEdit }) {
  const { RecuiterStore, CommonStore } = useStores();
  const [questions, setQuestions] = useState(
    RecuiterStore.jobPostDetails.basicJobDetails.prescreeningQuestions.length >
      0
      ? RecuiterStore.jobPostDetails.basicJobDetails.prescreeningQuestions
      : defaultQuestions()
  );
  const [skipped, setSkipped] = useState(
    RecuiterStore.jobPostDetails.basicJobDetails.prescreeningQuestions
      .length === 0 &&
      (RecuiterStore.jobPostDetails.basicJobDetails.prescreeningQuestions
        .skipped ||
        false)
  );
  const [showPopup, setShowPopup] = useState(false);

  // Sync MobX store on change
  const updateStore = (newQuestions) => {
    setQuestions(newQuestions);
    RecuiterStore.setPrescreeningQuestions(newQuestions);
  };

  const handleAddQuestion = () => {
    updateStore([...questions, emptyQuestion()]);
  };

  const handleRemoveQuestion = (idx) => {
    const newQuestions = questions.filter((_, i) => i !== idx);
    updateStore(newQuestions);
  };

  const handleChange = (idx, key, value) => {
    const newQuestions = [...questions];
    if (key === "type") {
      // Reset options/expectedAnswer for type change
      if (value === "yes/No") {
        newQuestions[idx] = {
          ...newQuestions[idx],
          type: value,
          options: { a: "Yes", b: "No" },
          expectedAnswer: "a",
        };
      } else if (value === "multipleChoice") {
        newQuestions[idx] = {
          ...newQuestions[idx],
          type: value,
          options: { a: "", b: "" },
          expectedAnswer: "a",
        };
      } else {
        newQuestions[idx] = {
          ...newQuestions[idx],
          type: value,
          options: null,
          expectedAnswer: null,
        };
      }
    } else if (key === "options") {
      newQuestions[idx].options = value;
    } else {
      newQuestions[idx][key] = value;
    }
    updateStore(newQuestions);
  };

  const handleOptionChange = (qIdx, optKey, val) => {
    const opts = { ...questions[qIdx].options, [optKey]: val };
    handleChange(qIdx, "options", opts);
  };

  const handleAddOption = (qIdx) => {
    const opts = { ...questions[qIdx].options };
    const nextKey = defaultOptionKeys.find((k) => !(k in opts));
    if (nextKey) opts[nextKey] = "";
    handleChange(qIdx, "options", opts);
  };

  const handleRemoveOption = (qIdx, optKey) => {
    const opts = { ...questions[qIdx].options };
    delete opts[optKey];
    handleChange(qIdx, "options", opts);
  };

  const handleMoveOptionUp = (qIdx, optKey) => {
    const opts = { ...questions[qIdx].options };
    const keys = Object.keys(opts);
    const currentIndex = keys.indexOf(optKey);
    if (currentIndex > 0) {
      const newOpts = {};
      keys.forEach((key, idx) => {
        if (idx === currentIndex - 1) {
          newOpts[optKey] = opts[optKey];
        } else if (idx === currentIndex) {
          newOpts[keys[currentIndex - 1]] = opts[keys[currentIndex - 1]];
        } else {
          newOpts[key] = opts[key];
        }
      });
      handleChange(qIdx, "options", newOpts);
    }
  };

  const handleMoveOptionDown = (qIdx, optKey) => {
    const opts = { ...questions[qIdx].options };
    const keys = Object.keys(opts);
    const currentIndex = keys.indexOf(optKey);
    if (currentIndex < keys.length - 1) {
      const newOpts = {};
      keys.forEach((key, idx) => {
        if (idx === currentIndex) {
          newOpts[keys[currentIndex + 1]] = opts[keys[currentIndex + 1]];
        } else if (idx === currentIndex + 1) {
          newOpts[optKey] = opts[optKey];
        } else {
          newOpts[key] = opts[key];
        }
      });
      handleChange(qIdx, "options", newOpts);
    }
  };

  const UpArrowIcon = () => (
    <svg
      width="16"
      height="16"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 15l7-7 7 7"
      />
    </svg>
  );

  const DownArrowIcon = () => (
    <svg
      width="16"
      height="16"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );

  const validate = () => {
    for (const q of questions) {
      if (!q.question.trim()) return false;
      if (
        (q.type === "yes/No" || q.type === "multipleChoice") &&
        (!q.options || Object.values(q.options).some((v) => !v))
      )
        return false;
      if (
        (q.type === "yes/No" || q.type === "multipleChoice") &&
        !q.expectedAnswer
      )
        return false;
    }
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (skipped) {
      RecuiterStore.setPrescreeningQuestions([]);
      CommonStore.setProgress(2); // Go to MCQ step
      CommonStore.setLabelActive(1, false);
      CommonStore.setLabelSkipped(1, true);
      return;
    }
    if (!validate()) {
      alert("Please fill all required fields for each question.");
      return;
    }
    RecuiterStore.setPrescreeningQuestions(toJS(questions));
    console.log(
      toJS(RecuiterStore.jobPostDetails.basicJobDetails),
      "questions11"
    );
    console.log(questions, "questions1001");
    CommonStore.setProgress(2); // Go to MCQ step
    CommonStore.setLabelActive(1, true);
    CommonStore.setLabelSkipped(1, false);
    console.log(questions, "questions11");
  };

  const handleBack = () => {
    CommonStore.setProgress(0);
  };

  const handleSkip = () => {
    setShowPopup(true);
  };

  const handleSkipConfirm = () => {
    setSkipped(true);
    updateStore([]);
    RecuiterStore.setPrescreeningQuestions([]);
    CommonStore.setProgress(2);
    CommonStore.setLabelActive(1, false);
    CommonStore.setLabelSkipped(1, true);
    setShowPopup(false);
  };

  // If user edits or adds a question, unskip
  const handleUnskip = () => {
    if (skipped) {
      setSkipped(false);
      updateStore(defaultQuestions());
      CommonStore.setLabelSkipped(1, false);
    }
  };

  return useObserver(() => (
    <form className="w-[90%] mx-auto mt-8" onSubmit={handleNext}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-primary">
          Prescreening Questions
        </h2>
        {!skipped && (
          <button
            type="button"
            className="text-primary underline font-bold underline-offset-2"
            onClick={handleSkip}
          >
            Skip Section
          </button>
        )}
        {skipped && (
          <span className="text-primary">This section is skipped</span>
        )}
      </div>
      {!skipped &&
        questions.map((q, idx) => (
          <div className="bg-white rounded-2xl shadow-md p-8 mb-10 relative border border-gray-200">
            <button
              className="absolute top-5 right-5 text-red-400 hover:text-red-600"
              onClick={() => {
                handleRemoveQuestion(idx);
                handleUnskip();
              }}
            >
              <TrashIcon />
            </button>
            <div className="flex flex-col gap-0">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="w-full sm:w-2/3">
                  <label className="font-semibold text-lg mb-2 block">
                    Question {idx + 1}
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 mt-1 text-base focus:outline-primary"
                    type="text"
                    value={q.question}
                    onChange={(e) => {
                      handleChange(idx, "question", e.target.value);
                      handleUnskip();
                    }}
                    placeholder="Enter your question"
                    required
                  />
                </div>
                <div className="w-full sm:w-1/3">
                  <label className="font-semibold text-lg mb-2 block">
                    Question Type
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 mt-1 text-base focus:outline-primary"
                    value={q.type}
                    onChange={(e) => {
                      handleChange(idx, "type", e.target.value);
                      handleUnskip();
                    }}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <hr className="my-4 border-gray-200" />
              {(q.type === "yes/No" || q.type === "multipleChoice") && (
                <>
                  <label className="font-semibold text-lg mb-3 block">
                    Options
                  </label>
                  <div className="flex flex-col gap-3 bg-gray-50 p-5 rounded-xl">
                    {q.options &&
                      Object.entries(q.options).map(
                        ([optKey, optVal], oIdx) => (
                          <div
                            key={optKey}
                            className="md:flex items-center gap-3 w-full"
                          >
                            <input
                              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-base"
                              type="text"
                              value={optVal}
                              onChange={(e) => {
                                handleOptionChange(idx, optKey, e.target.value);
                                handleUnskip();
                              }}
                              placeholder={`Option ${optKey.toUpperCase()}`}
                              required
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-gray-400 hover:text-primary p-1"
                                onClick={() => {
                                  handleMoveOptionUp(idx, optKey);
                                  handleUnskip();
                                }}
                                disabled={oIdx === 0}
                              >
                                <UpArrowIcon />
                              </button>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-primary p-1"
                                onClick={() => {
                                  handleMoveOptionDown(idx, optKey);
                                  handleUnskip();
                                }}
                                disabled={
                                  oIdx === Object.keys(q.options).length - 1
                                }
                              >
                                <DownArrowIcon />
                              </button>
                              {q.type === "multipleChoice" &&
                                Object.keys(q.options).length > 2 && (
                                  <button
                                    type="button"
                                    className="text-red-400 hover:text-red-600 p-1"
                                    onClick={() => {
                                      handleRemoveOption(idx, optKey);
                                      handleUnskip();
                                    }}
                                  >
                                    <TrashIcon />
                                  </button>
                                )}
                              <input
                                type="radio"
                                name={`expectedAnswer-${idx}`}
                                checked={q.expectedAnswer === optKey}
                                onChange={() => {
                                  handleChange(idx, "expectedAnswer", optKey);
                                  handleUnskip();
                                }}
                              />
                              <span className="text-xs sm:text-base">
                                Correct
                              </span>
                            </div>
                          </div>
                        )
                      )}
                    {q.type === "multipleChoice" &&
                      Object.keys(q.options).length < 4 && (
                        <button
                          type="button"
                          className="text-primary mt-2 text-base font-semibold"
                          onClick={() => {
                            handleAddOption(idx);
                            handleUnskip();
                          }}
                        >
                          + Add Option
                        </button>
                      )}
                  </div>
                </>
              )}
              <div className="flex items-center mt-6 mb-2">
                <input
                  type="checkbox"
                  checked={q.disqualifyIfIncorrect}
                  onChange={(e) => {
                    handleChange(
                      idx,
                      "disqualifyIfIncorrect",
                      e.target.checked
                    );
                    handleUnskip();
                  }}
                />
                <span className="ml-3 text-base">Disqualify If Incorrect</span>
              </div>
            </div>
          </div>
        ))}
      {!skipped && (
        <button
          type="button"
          className="text-primary font-semibold mb-8 w-full py-3 rounded-lg bg-white shadow-sm text-base sm:text-lg"
          onClick={() => {
            handleAddQuestion();
            handleUnskip();
          }}
        >
          + Add Question
        </button>
      )}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pb-20 w-full">
        <button
          type="button"
          className="flex items-center justify-center text-primary gap-2 w-full sm:w-auto py-3 rounded-lg bg-white shadow-sm text-base sm:text-lg"
          onClick={handleBack}
        >
          Back
        </button>
        <PrimaryButton
          buttonName={skipped ? "Next" : "Next"}
          width="w-full sm:w-fit"
          type={"submit"}
          padding={10}
          rounded="lg"
        />
      </div>
      {showPopup && (
        <Popup
          onSkip={handleSkipConfirm}
          onFillDetails={() => setShowPopup(false)}
        />
      )}
    </form>
  ));
}
